<template>
  <aside class="sidebar">
    <section class="panel" data-tutorial="point-panel">
      <button class="panel-toggle" type="button" @click="toggleSection('point')" :aria-expanded="String(sections.point)">
        <h2 class="panel-title">
          <FeatureTypeIcon type="point" />
          <span>Point</span>
        </h2>
        <span>{{ sections.point ? '−' : '+' }}</span>
      </button>
      <template v-if="sections.point">
        <div class="tool-chooser">
          <div class="tool-chooser-label">Point input type</div>
          <div class="tool-radio-grid point-mode-grid" role="radiogroup" aria-label="Point input type">
            <button
              class="tool-radio"
              :class="{ active: pointMode === 'single' }"
              type="button"
              role="radio"
              title="Single"
              data-point-mode="single"
              :aria-checked="String(pointMode === 'single')"
              :tabindex="pointMode === 'single' ? 0 : -1"
              @click="pointMode = 'single'"
              @keydown="handlePointModeKeydown($event, 'single')"
            >
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Single</span>
                <span class="tool-radio-help">One named coordinate</span>
              </span>
            </button>
            <button
              class="tool-radio"
              :class="{ active: pointMode === 'batch' }"
              type="button"
              role="radio"
              title="Batch"
              data-point-mode="batch"
              :aria-checked="String(pointMode === 'batch')"
              :tabindex="pointMode === 'batch' ? 0 : -1"
              @click="pointMode = 'batch'"
              @keydown="handlePointModeKeydown($event, 'batch')"
            >
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Batch</span>
                <span class="tool-radio-help">Paste NAME LAT LON rows</span>
              </span>
            </button>
          </div>
        </div>

        <template v-if="pointMode === 'single'">
          <div class="field">
            <label for="marker-name">Name</label>
            <input id="marker-name" v-model="marker.name" type="text" :placeholder="`e.g. POI_${poiCounter}`" />
          </div>

          <div class="field">
            <label for="marker-description">Description</label>
            <input id="marker-description" v-model="marker.description" type="text" placeholder="Optional" />
          </div>

          <div class="field">
            <label for="marker-coord">Coordinate (any supported format)</label>
            <input id="marker-coord" v-model="marker.coordinateText" type="text" placeholder="-32.9283, 151.7817" />
          </div>

          <div class="field">
            <label for="marker-color-text">Colour</label>
            <div class="color-row">
              <input id="marker-color-text" v-model="marker.color" type="text" placeholder="#000000" />
              <input v-model="marker.color" type="color" class="color-picker" aria-label="Pick point colour" />
            </div>
          </div>

          <div class="field">
            <label>Icon</label>
            <IconPicker v-model="marker.iconUrl" :icons="kmlIcons" />
          </div>

          <div class="row">
            <button class="primary" data-action="add-point-single" type="button" @click="addMarker">Add</button>
            <button class="secondary" type="button" @click="clearMarker">Clear</button>
          </div>

          <p v-if="markerError" class="error">{{ markerError }}</p>
          <p v-if="markerSuccess" class="success">{{ markerSuccess }}</p>
        </template>

        <template v-else>
          <div class="field">
            <label for="batch-point-description">Description</label>
            <input id="batch-point-description" v-model="batchMarker.description" type="text" placeholder="Optional shared description" />
          </div>

          <div class="field">
            <label for="batch-point-color-text">Colour</label>
            <div class="color-row">
              <input id="batch-point-color-text" v-model="batchMarker.color" type="text" placeholder="#000000" />
              <input v-model="batchMarker.color" type="color" class="color-picker" aria-label="Pick batch point colour" />
            </div>
          </div>

          <div class="field">
            <label>Icon</label>
            <IconPicker v-model="batchMarker.iconUrl" :icons="kmlIcons" aria-label="Batch point icon picker" />
          </div>

          <div class="field">
            <label for="batch-point-list">Points (one NAME COORDINATE row per line)</label>
            <textarea id="batch-point-list" v-model="batchMarker.pointsText" class="coords-input" placeholder="ALPHA -32.9283 151.7817"></textarea>
          </div>

          <ul v-if="batchPreview.rows.length" class="preview-list batch-preview">
            <li v-for="row in batchPreview.rows" :key="`batch-${row.lineNo}`" :class="{ bad: !row.ok }">
              L{{ row.lineNo }}: <span>{{ row.source }}</span> — <span>{{ row.ok ? `${row.name}: ${row.ddm}` : row.error }}</span>
            </li>
          </ul>

          <div class="row batch-actions">
            <button class="primary" data-action="add-point-batch" type="button" @click="addMarkerBatch">Add batch</button>
            <button class="secondary" data-action="clear-point-batch" type="button" @click="clearMarkerBatch">Clear</button>
          </div>

          <p v-if="batchMarkerError" class="error">{{ batchMarkerError }}</p>
          <p v-if="batchMarkerSuccess" class="success">{{ batchMarkerSuccess }}</p>
        </template>
      </template>
    </section>

    <section class="panel" data-tutorial="path-panel">
      <button class="panel-toggle" type="button" @click="toggleSection('path')" :aria-expanded="String(sections.path)">
        <h2 class="panel-title">
          <FeatureTypeIcon type="path" />
          <span>Path</span>
        </h2>
        <span>{{ sections.path ? '−' : '+' }}</span>
      </button>
      <template v-if="sections.path">
        <div class="tool-chooser">
          <div class="tool-chooser-label">Type</div>
          <div class="tool-radio-grid" role="radiogroup" aria-label="Path tool selector">
            <button class="tool-radio" :class="{ active: pathMode === 'path' }" type="button" role="radio" :aria-checked="String(pathMode === 'path')" @click="pathMode = 'path'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Path</span>
                <span class="tool-radio-help">Manual coordinate list</span>
              </span>
            </button>
            <button class="tool-radio" :class="{ active: pathMode === 'arc' }" type="button" role="radio" :aria-checked="String(pathMode === 'arc')" @click="pathMode = 'arc'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Arc</span>
                <span class="tool-radio-help">Center, radius, bearings</span>
              </span>
            </button>
          </div>
        </div>

        <div class="field">
          <label for="path-name">Name</label>
          <input id="path-name" v-model="activePathFields.name" type="text" :placeholder="pathMode === 'path' ? 'e.g. Track 1' : 'e.g. Final arc'" />
        </div>

        <div class="field">
          <label for="path-description">Description</label>
          <input id="path-description" v-model="activePathFields.description" type="text" placeholder="Optional" />
        </div>

        <div class="field">
          <label for="path-color-text">Colour</label>
          <div class="color-row">
            <input id="path-color-text" v-model="activePathFields.color" type="text" :placeholder="pathMode === 'path' ? '#0066ff' : '#ff7a00'" />
            <input v-model="activePathFields.color" type="color" class="color-picker" :aria-label="pathMode === 'path' ? 'Pick path colour' : 'Pick arc colour'" />
          </div>
        </div>

        <template v-if="pathMode === 'path'">
          <textarea class="coords-input" v-model="path.coordsText" placeholder="One coordinate per line"></textarea>

          <div class="row">
            <button class="primary" type="button" @click="addPath">Add</button>
            <button class="secondary" type="button" @click="clearPath">Clear</button>
          </div>

          <p v-if="pathError" class="error">{{ pathError }}</p>
          <p v-if="pathSuccess" class="success">{{ pathSuccess }}</p>
          <ul v-if="pathPreview.length" class="preview-list">
            <li v-for="row in pathPreview" :key="`path-${row.lineNo}`" :class="{ bad: !row.ok }">
              L{{ row.lineNo }}: <span>{{ row.ok ? row.ddm : row.error }}</span>
            </li>
          </ul>
        </template>

        <template v-else>
          <div class="field">
            <label for="arc-center">Center coordinate (any supported format)</label>
            <input id="arc-center" v-model="arc.centerText" type="text" placeholder="-32.9283, 151.7817" />
          </div>

          <div class="grid3 wide-grid">
            <div class="field">
              <label for="arc-radius">Radius</label>
              <input id="arc-radius" v-model.number="arc.radius" type="number" min="0" step="0.1" placeholder="5" />
            </div>
            <div class="field">
              <label for="arc-unit">Unit</label>
              <select id="arc-unit" v-model="arc.radiusUnit">
                <option v-for="unit in circleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
              </select>
            </div>
            <div class="field">
              <label for="arc-direction">Direction</label>
              <select id="arc-direction" v-model="arc.direction">
                <option v-for="direction in arcDirections" :key="direction.value" :value="direction.value">{{ direction.label }}</option>
              </select>
            </div>
          </div>

          <div class="grid3 wide-grid">
            <div class="field">
              <label for="arc-start-bearing">Start bearing</label>
              <input id="arc-start-bearing" v-model.number="arc.startBearing" type="number" step="1" placeholder="0" />
            </div>
            <div class="field">
              <label for="arc-end-bearing">End bearing</label>
              <input id="arc-end-bearing" v-model.number="arc.endBearing" type="number" step="1" placeholder="90" />
            </div>
          </div>

          <div class="row">
            <button class="primary" type="button" @click="addArc">Add arc</button>
            <button class="secondary" type="button" @click="clearArc">Clear</button>
          </div>

          <p v-if="arcError" class="error">{{ arcError }}</p>
          <p v-if="arcSuccess" class="success">{{ arcSuccess }}</p>
        </template>
      </template>
    </section>

    <section class="panel" data-tutorial="polygon-panel">
      <button class="panel-toggle" type="button" @click="toggleSection('polygon')" :aria-expanded="String(sections.polygon)">
        <h2 class="panel-title">
          <FeatureTypeIcon type="polygon" />
          <span>Polygon</span>
        </h2>
        <span>{{ sections.polygon ? '−' : '+' }}</span>
      </button>
      <template v-if="sections.polygon">
        <div class="tool-chooser">
          <div class="tool-chooser-label">Create shape type</div>
          <div class="tool-radio-grid tool-radio-grid-polygon" role="radiogroup" aria-label="Polygon tool selector">
            <button class="tool-radio" :class="{ active: polygonMode === 'polygon' }" type="button" role="radio" :aria-checked="String(polygonMode === 'polygon')" @click="polygonMode = 'polygon'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Polygon</span>
                <span class="tool-radio-help">List of coordinates</span>
              </span>
            </button>
            <button class="tool-radio" :class="{ active: polygonMode === 'freestyle' }" type="button" role="radio" :aria-checked="String(polygonMode === 'freestyle')" @click="polygonMode = 'freestyle'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Freestyle</span>
                <span class="tool-radio-help">Sketch directly on the map</span>
              </span>
            </button>
            <button class="tool-radio" :class="{ active: polygonMode === 'circle' }" type="button" role="radio" :aria-checked="String(polygonMode === 'circle')" @click="polygonMode = 'circle'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Circle</span>
                <span class="tool-radio-help">Center and radius</span>
              </span>
            </button>
            <button class="tool-radio" :class="{ active: polygonMode === 'cone' }" type="button" role="radio" :aria-checked="String(polygonMode === 'cone')" @click="polygonMode = 'cone'">
              <span class="tool-radio-dot" aria-hidden="true"></span>
              <span class="tool-radio-copy">
                <span class="tool-radio-title">Cone</span>
                <span class="tool-radio-help">Center, radius, bearings</span>
              </span>
            </button>
          </div>
        </div>

        <div class="field">
          <label for="poly-name">Name</label>
          <input id="poly-name" v-model="activePolygonFields.name" type="text" :placeholder="polygonNamePlaceholder" />
        </div>

        <div class="field">
          <label for="poly-description">Description</label>
          <input id="poly-description" v-model="activePolygonFields.description" type="text" placeholder="Optional" />
        </div>

        <div class="grid3">
          <div class="field">
            <label for="poly-color-text">Colour</label>
            <div class="color-row">
              <input id="poly-color-text" v-model="activePolygonFields.color" type="text" :placeholder="polygonColorPlaceholder" />
              <input v-model="activePolygonFields.color" type="color" class="color-picker" :aria-label="polygonColorAriaLabel" />
            </div>
          </div>
          <div class="field">
            <label for="poly-opacity">Opacity</label>
            <input id="poly-opacity" v-model.number="activePolygonFields.opacity" type="number" min="0" max="1" step="0.05" />
          </div>
        </div>

        <template v-if="polygonMode === 'polygon'">
          <textarea class="coords-input" v-model="poly.coordsText" placeholder="One coordinate per line"></textarea>

          <div class="row">
            <button class="primary" type="button" @click="addPolygon">Add</button>
            <button class="secondary" type="button" @click="clearPoly">Clear</button>
          </div>

          <p v-if="polyError" class="error">{{ polyError }}</p>
          <p v-if="polySuccess" class="success">{{ polySuccess }}</p>
          <ul v-if="polyPreview.length" class="preview-list">
            <li v-for="row in polyPreview" :key="`poly-${row.lineNo}`" :class="{ bad: !row.ok }">
              L{{ row.lineNo }}: <span>{{ row.ok ? row.ddm : row.error }}</span>
            </li>
          </ul>
        </template>

        <template v-else-if="polygonMode === 'freestyle'">
          <p class="helper-text">Use the shared polygon fields above, then click the map to sketch the shape.</p>

          <div class="row" style="margin-bottom: 8px;">
            <button
              class="primary"
              data-testid="freestyle-toggle"
              type="button"
              @click="manualDrawActive ? finishManualPolygonDraw() : startManualPolygonDraw()"
            >
              {{ manualDrawActive ? "Finish" : "Begin" }}
            </button>
            <button class="secondary" type="button" @click="cancelManualPolygonDraw" :disabled="!manualDrawActive">Cancel</button>
          </div>
          <p v-if="manualDrawActive" class="success">Freestyle polygon active: {{ manualDrawVertexCount }} vertices captured. Click on map to add vertices.</p>
        </template>

        <template v-else-if="polygonMode === 'circle'">
          <div class="field">
            <label for="circle-center">Center coordinate (any supported format)</label>
            <input id="circle-center" v-model="circle.centerText" type="text" placeholder="-32.9283, 151.7817" />
          </div>

          <div class="grid3">
            <div class="field">
              <label for="circle-radius">Radius</label>
              <input id="circle-radius" v-model.number="circle.radius" type="number" min="0" step="0.1" placeholder="5" />
            </div>
            <div class="field">
              <label for="circle-unit">Unit</label>
              <select id="circle-unit" v-model="circle.radiusUnit">
                <option v-for="unit in circleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
              </select>
            </div>
          </div>

          <div class="row">
            <button class="primary" type="button" @click="addCircle">Add circle</button>
            <button class="secondary" type="button" @click="clearCircle">Clear</button>
          </div>

          <p v-if="circleError" class="error">{{ circleError }}</p>
          <p v-if="circleSuccess" class="success">{{ circleSuccess }}</p>
        </template>

        <template v-else>
          <div class="field">
            <label for="sector-center">Cone center coordinate (any supported format)</label>
            <input id="sector-center" v-model="sector.centerText" type="text" placeholder="-32.9283, 151.7817" />
          </div>

          <div class="grid3 wide-grid">
            <div class="field">
              <label for="sector-radius">Cone radius</label>
              <input id="sector-radius" v-model.number="sector.radius" type="number" min="0" step="0.1" placeholder="5" />
            </div>
            <div class="field">
              <label for="sector-unit">Unit</label>
              <select id="sector-unit" v-model="sector.radiusUnit">
                <option v-for="unit in circleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
              </select>
            </div>
            <div class="field">
              <label for="sector-direction">Direction</label>
              <select id="sector-direction" v-model="sector.direction">
                <option v-for="direction in arcDirections" :key="direction.value" :value="direction.value">{{ direction.label }}</option>
              </select>
            </div>
          </div>

          <div class="grid3 wide-grid">
            <div class="field">
              <label for="sector-start-bearing">Start bearing</label>
              <input id="sector-start-bearing" v-model.number="sector.startBearing" type="number" step="1" placeholder="0" />
            </div>
            <div class="field">
              <label for="sector-end-bearing">End bearing</label>
              <input id="sector-end-bearing" v-model.number="sector.endBearing" type="number" step="1" placeholder="90" />
            </div>
          </div>

          <div class="row">
            <button class="primary" type="button" @click="addSector">Add cone</button>
            <button class="secondary" type="button" @click="clearSector">Clear</button>
          </div>

          <p v-if="sectorError" class="error">{{ sectorError }}</p>
          <p v-if="sectorSuccess" class="success">{{ sectorSuccess }}</p>
        </template>
      </template>
    </section>

    <p class="cap">Features: <span class="feature-count-chip">{{ currentFeatureCount }}/{{ featureCap }}</span></p>
  </aside>
</template>

<script setup>
import { computed, reactive, ref } from "vue"
import { parseCoordinateLine, parseCoordinateListDetailed } from "@/utils/coordinates"
import { buildBatchPointFeatures, parseBatchPoints } from "@/utils/batchPoints"
import { ARC_DIRECTIONS, buildArcFeature, buildCircleFeature, buildSectorFeature, CIRCLE_UNITS } from "@/utils/circles"
import { GOOGLE_KML_ICONS } from "@/utils/kmlIcons"
import FeatureTypeIcon from "@/components/FeatureTypeIcon.vue"
import IconPicker from "@/components/IconPicker.vue"

const props = defineProps({
  featureCap: { type: Number, default: 500 },
  currentFeatureCount: { type: Number, default: 0 },
  manualDrawActive: { type: Boolean, default: false },
  manualDrawVertexCount: { type: Number, default: 0 },
})

const emit = defineEmits(["add-feature", "add-features", "start-polygon-draw", "finish-polygon-draw", "cancel-polygon-draw"])

const marker = reactive({ name: "", description: "", coordinateText: "", color: "#000000", iconUrl: GOOGLE_KML_ICONS[0].url })
const batchMarker = reactive({ description: "", color: "#000000", iconUrl: GOOGLE_KML_ICONS[0].url, pointsText: "" })
const path = reactive({ name: "", description: "", color: "#0066ff", coordsText: "" })
const poly = reactive({ name: "", description: "", color: "#00aa66", opacity: 0.25, coordsText: "" })
const sector = reactive({ name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", startBearing: 0, endBearing: 90, direction: "cw", color: "#00aa66", opacity: 0.25 })
const circle = reactive({ name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", color: "#0080ff", opacity: 0.25 })
const arc = reactive({ name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", startBearing: 0, endBearing: 90, direction: "cw", color: "#ff7a00" })
const sections = reactive({ point: false, path: false, polygon: false })
const pointMode = ref("single")
const pathMode = ref("path")
const polygonMode = ref("polygon")

const markerError = ref("")
const batchMarkerError = ref("")
const pathError = ref("")
const polyError = ref("")
const sectorError = ref("")
const circleError = ref("")
const arcError = ref("")
const markerSuccess = ref("")
const batchMarkerSuccess = ref("")
const pathSuccess = ref("")
const polySuccess = ref("")
const sectorSuccess = ref("")
const circleSuccess = ref("")
const arcSuccess = ref("")

const poiCounter = computed(() => props.currentFeatureCount + 1)
const batchPreview = computed(() => parseBatchPoints(batchMarker.pointsText))
const pathPreview = computed(() => parseCoordinateListDetailed(path.coordsText).parsedLines)
const polyPreview = computed(() => parseCoordinateListDetailed(poly.coordsText).parsedLines)

const kmlIcons = GOOGLE_KML_ICONS
const circleUnits = CIRCLE_UNITS
const arcDirections = ARC_DIRECTIONS
const activePathFields = computed(() => (pathMode.value === "path" ? path : arc))
const activePolygonFields = computed(() => {
  if (polygonMode.value === "circle") return circle
  if (polygonMode.value === "cone") return sector
  return poly
})
const polygonNamePlaceholder = computed(() => {
  if (polygonMode.value === "freestyle") return "e.g. Area sketch"
  if (polygonMode.value === "circle") return "e.g. Hold area"
  if (polygonMode.value === "cone") return "e.g. Cone area"
  return "e.g. Area 1"
})
const polygonColorPlaceholder = computed(() => {
  if (polygonMode.value === "circle") return "#0080ff"
  return "#00aa66"
})
const polygonColorAriaLabel = computed(() => {
  if (polygonMode.value === "circle") return "Pick circle colour"
  if (polygonMode.value === "cone") return "Pick cone colour"
  return "Pick polygon colour"
})

const pointModes = ["single", "batch"]

function handlePointModeKeydown(event, currentMode) {
  const currentIndex = pointModes.indexOf(currentMode)
  let nextIndex
  if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (currentIndex + 1) % pointModes.length
  else if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (currentIndex - 1 + pointModes.length) % pointModes.length
  else if (event.key === "Home") nextIndex = 0
  else if (event.key === "End") nextIndex = pointModes.length - 1
  else return

  event.preventDefault()
  pointMode.value = pointModes[nextIndex]
  event.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll('[role="radio"]')[nextIndex]?.focus()
}

function toggleSection(target) {
  const nextValue = !sections[target]
  Object.keys(sections).forEach((key) => {
    sections[key] = key === target ? nextValue : false
  })
  if (target === "path" && nextValue && !["path", "arc"].includes(pathMode.value)) pathMode.value = "path"
  if (target === "polygon" && nextValue && !["polygon", "freestyle", "circle", "cone"].includes(polygonMode.value)) polygonMode.value = "polygon"
}

function canAddFeature(errorRef) {
  if (props.currentFeatureCount >= props.featureCap) {
    errorRef.value = `Feature cap reached (${props.featureCap}). Delete an item before adding another.`
    return false
  }
  return true
}

function newId() {
  return crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())
}

function normalizeColor(input) {
  const s = String(input || "").trim()
  if (!s) return { ok: true, value: undefined }
  if (/^#([0-9a-fA-F]{6})$/.test(s)) return { ok: true, value: s }
  return { ok: false, error: "Colour must be a hex value like #RRGGBB." }
}

function closeRing(ring) {
  if (ring.length < 3) return ring
  const first = ring[0]
  const last = ring[ring.length - 1]
  return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first]
}

function formatRadius(value) {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? String(numeric) : String(value || "")
}

function addMarker() {
  markerError.value = ""
  markerSuccess.value = ""
  if (!canAddFeature(markerError)) return

  const parsed = parseCoordinateLine(marker.coordinateText)
  if (parsed.error) return (markerError.value = parsed.error)

  const col = normalizeColor(marker.color)
  if (!col.ok) return (markerError.value = col.error)

  const selectedIcon = GOOGLE_KML_ICONS.find((i) => i.url === marker.iconUrl) || GOOGLE_KML_ICONS[0]

  emit("add-feature", {
    type: "Feature",
    id: newId(),
    properties: {
      featureType: "marker",
      name: marker.name.trim() || `POI_${poiCounter.value}`,
      description: marker.description.trim() || "",
      color: col.value || "#000000",
      iconUrl: selectedIcon.url,
      iconSymbol: selectedIcon.symbol,
      iconLabel: selectedIcon.label,
    },
    geometry: { type: "Point", coordinates: [parsed.lng, parsed.lat] },
  })

  markerSuccess.value = `Point added (${parsed.ddm}).`
}

function addMarkerBatch() {
  batchMarkerError.value = ""
  batchMarkerSuccess.value = ""

  const parsed = parseBatchPoints(batchMarker.pointsText)
  if (parsed.error) return (batchMarkerError.value = parsed.error)

  const col = normalizeColor(batchMarker.color)
  if (!col.ok) return (batchMarkerError.value = col.error)

  const remaining = Math.max(0, props.featureCap - props.currentFeatureCount)
  if (parsed.validRows.length > remaining) {
    return (batchMarkerError.value = `Only ${remaining} feature slots remain; batch contains ${parsed.validRows.length} points.`)
  }

  const selectedIcon = GOOGLE_KML_ICONS.find((icon) => icon.url === batchMarker.iconUrl) || GOOGLE_KML_ICONS[0]
  const features = buildBatchPointFeatures(parsed.validRows, {
    description: batchMarker.description.trim(),
    color: col.value || "#000000",
    iconUrl: selectedIcon.url,
    iconSymbol: selectedIcon.symbol,
    iconLabel: selectedIcon.label,
  }, newId)

  const acknowledge = (result) => {
    batchMarkerError.value = ""
    batchMarkerSuccess.value = ""
    if (result?.ok) {
      batchMarkerSuccess.value = `Points added (${result.addedCount}).`
      return
    }
    batchMarkerError.value = String(result?.error || "Could not add points.")
  }

  emit("add-features", features, acknowledge)
}

function addPath() {
  pathError.value = ""
  pathSuccess.value = ""
  if (!canAddFeature(pathError)) return

  const col = normalizeColor(path.color)
  if (!col.ok) return (pathError.value = col.error)

  const parsed = parseCoordinateListDetailed(path.coordsText)
  if (parsed.error) return (pathError.value = parsed.error)
  if (parsed.coords.length < 2) return (pathError.value = "A path needs at least 2 coordinates.")

  emit("add-feature", {
    type: "Feature",
    id: newId(),
    properties: {
      featureType: "path",
      name: path.name.trim() || "Path",
      description: path.description.trim() || "",
      color: col.value,
    },
    geometry: { type: "LineString", coordinates: parsed.coords },
  })

  pathSuccess.value = `Path added (${parsed.coords.length} points).`
}

function addPolygon() {
  polyError.value = ""
  polySuccess.value = ""
  if (!canAddFeature(polyError)) return

  const col = normalizeColor(poly.color)
  if (!col.ok) return (polyError.value = col.error)

  const parsed = parseCoordinateListDetailed(poly.coordsText)
  if (parsed.error) return (polyError.value = parsed.error)
  if (parsed.coords.length < 3) return (polyError.value = "A polygon needs at least 3 coordinates.")

  emit("add-feature", {
    type: "Feature",
    id: newId(),
    properties: {
      featureType: "polygon",
      name: poly.name.trim() || "Polygon",
      description: poly.description.trim() || "",
      color: col.value,
      opacity: Math.min(1, Math.max(0, Number(poly.opacity || 0.25))),
    },
    geometry: { type: "Polygon", coordinates: [closeRing(parsed.coords)] },
  })

  polySuccess.value = `Polygon added (${parsed.coords.length} vertices).`
}

function addSector() {
  sectorError.value = ""
  sectorSuccess.value = ""
  if (!canAddFeature(sectorError)) return

  const center = parseCoordinateLine(sector.centerText)
  if (center.error) return (sectorError.value = center.error)

  const col = normalizeColor(sector.color)
  if (!col.ok) return (sectorError.value = col.error)

  const feature = buildSectorFeature({
    id: newId(),
    name: sector.name.trim() || "Cone",
    description: sector.description.trim() || "",
    color: col.value || "#00aa66",
    opacity: Math.min(1, Math.max(0, Number(sector.opacity || 0.25))),
    centerLng: center.lng,
    centerLat: center.lat,
    radius: sector.radius,
    radiusUnit: sector.radiusUnit,
    startBearing: sector.startBearing,
    endBearing: sector.endBearing,
    direction: sector.direction,
  })

  if (feature.error) return (sectorError.value = feature.error)

  emit("add-feature", feature)
  sectorSuccess.value = `Cone added (${formatRadius(sector.radius)} ${feature.properties.radiusUnit}, ${feature.properties.arcDirection.toUpperCase()} ${Math.round(feature.properties.arcSweepDegrees)}°).`
}

function addCircle() {
  circleError.value = ""
  circleSuccess.value = ""
  if (!canAddFeature(circleError)) return

  const center = parseCoordinateLine(circle.centerText)
  if (center.error) return (circleError.value = center.error)

  const col = normalizeColor(circle.color)
  if (!col.ok) return (circleError.value = col.error)

  const feature = buildCircleFeature({
    id: newId(),
    name: circle.name.trim() || "Circle",
    description: circle.description.trim() || "",
    color: col.value || "#0080ff",
    opacity: Math.min(1, Math.max(0, Number(circle.opacity || 0.25))),
    centerLng: center.lng,
    centerLat: center.lat,
    radius: circle.radius,
    radiusUnit: circle.radiusUnit,
  })

  if (feature.error) return (circleError.value = feature.error)

  emit("add-feature", feature)
  circleSuccess.value = `Circle added (${formatRadius(circle.radius)} ${feature.properties.radiusUnit} radius).`
}

function addArc() {
  arcError.value = ""
  arcSuccess.value = ""
  if (!canAddFeature(arcError)) return

  const center = parseCoordinateLine(arc.centerText)
  if (center.error) return (arcError.value = center.error)

  const col = normalizeColor(arc.color)
  if (!col.ok) return (arcError.value = col.error)

  const feature = buildArcFeature({
    id: newId(),
    name: arc.name.trim() || "Arc",
    description: arc.description.trim() || "",
    color: col.value || "#ff7a00",
    centerLng: center.lng,
    centerLat: center.lat,
    radius: arc.radius,
    radiusUnit: arc.radiusUnit,
    startBearing: arc.startBearing,
    endBearing: arc.endBearing,
    direction: arc.direction,
  })

  if (feature.error) return (arcError.value = feature.error)

  emit("add-feature", feature)
  arcSuccess.value = `Arc added (${formatRadius(arc.radius)} ${feature.properties.radiusUnit}, ${feature.properties.arcDirection.toUpperCase()} ${Math.round(feature.properties.arcSweepDegrees)}°).`
}

function startManualPolygonDraw() {
  emit("start-polygon-draw")
}

function finishManualPolygonDraw() {
  emit("finish-polygon-draw", {
    name: poly.name,
    description: poly.description,
    color: poly.color,
    opacity: poly.opacity,
  })
}

function cancelManualPolygonDraw() {
  emit("cancel-polygon-draw")
}

function clearMarker() {
  Object.assign(marker, { name: "", description: "", coordinateText: "", color: "#000000", iconUrl: GOOGLE_KML_ICONS[0].url })
  markerError.value = ""
  markerSuccess.value = ""
}
function clearMarkerBatch() {
  Object.assign(batchMarker, { description: "", color: "#000000", iconUrl: GOOGLE_KML_ICONS[0].url, pointsText: "" })
  batchMarkerError.value = ""
  batchMarkerSuccess.value = ""
}
function clearPath() {
  Object.assign(path, { name: "", description: "", color: "#0066ff", coordsText: "" })
  pathError.value = ""
  pathSuccess.value = ""
}
function clearPoly() {
  Object.assign(poly, { name: "", description: "", color: "#00aa66", opacity: 0.25, coordsText: "" })
  polyError.value = ""
  polySuccess.value = ""
}
function clearSector() {
  Object.assign(sector, { name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", startBearing: 0, endBearing: 90, direction: "cw", color: "#00aa66", opacity: 0.25 })
  sectorError.value = ""
  sectorSuccess.value = ""
}
function clearCircle() {
  Object.assign(circle, { name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", color: "#0080ff", opacity: 0.25 })
  circleError.value = ""
  circleSuccess.value = ""
}
function clearArc() {
  Object.assign(arc, { name: "", description: "", centerText: "", radius: "", radiusUnit: "nm", startBearing: 0, endBearing: 90, direction: "cw", color: "#ff7a00" })
  arcError.value = ""
  arcSuccess.value = ""
}
</script>

<style scoped>
.sidebar { border-right: 1px solid var(--color-border); background: var(--color-bg-subtle); padding: var(--space-3); overflow: auto; font-family: var(--font-sans); box-sizing: border-box; }
.panel { background: var(--color-bg-raised); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: var(--space-2); margin-bottom: var(--space-2); }
.panel-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  cursor: pointer;
  margin-bottom: var(--space-2);
}
.panel-title { display: flex; align-items: center; gap: var(--space-2); font-size: var(--fs-md); margin: 0; font-weight: 600; color: var(--color-text); }
.subsection-title { font-size: var(--fs-xs); font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--color-text-muted); margin: var(--space-1) 0 var(--space-2); }
.helper-text { margin: -2px 0 var(--space-2); font-size: var(--fs-xs); color: var(--color-text-muted); }
.tool-chooser { margin-bottom: var(--space-2); }
.tool-chooser-label { font-size: var(--fs-xs); font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--color-text-muted); margin: var(--space-1) 0 var(--space-2); }
.tool-radio-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-2); }
.point-mode-grid { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
.tool-radio {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}
.tool-radio:hover { background: var(--color-bg-subtle); border-color: var(--color-border-strong); }
.tool-radio.active {
  border-color: var(--color-accent);
  background: var(--color-bg-subtle);
  box-shadow: inset 0 0 0 1px var(--color-accent);
}
.tool-radio-dot {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid var(--color-border-strong);
  margin-top: 2px;
  flex: 0 0 auto;
  position: relative;
  background: var(--color-bg-raised);
}
.tool-radio.active .tool-radio-dot {
  border-color: var(--color-accent);
}
.tool-radio.active .tool-radio-dot::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 999px;
  background: var(--color-accent);
}
.tool-radio-copy { display: grid; gap: 2px; min-width: 0; }
.tool-radio-title { font-size: var(--fs-xs); font-weight: 600; color: var(--color-text); }
.tool-radio-help { font-size: var(--fs-xs); color: var(--color-text-muted); line-height: 1.25; }
.field { display: grid; gap: var(--space-1); margin-bottom: var(--space-2); }
.field label { font-size: var(--fs-xs); color: var(--color-text-muted); }
.field input,
.field select { width: 100%; box-sizing: border-box; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: var(--space-2); font-size: var(--fs-xs); background: var(--color-bg-raised); color: var(--color-text); font-family: var(--font-sans); }
.color-row { display: grid; grid-template-columns: 1fr 40px; gap: var(--space-2); align-items: center; }
.color-picker { width: 40px; height: 36px; border-radius: var(--radius-sm); border: 1px solid var(--color-border); padding: 2px; cursor: pointer; }
.grid3 { display: grid; grid-template-columns: minmax(0, 1fr) 120px; gap: var(--space-3); align-items: end; }
.wide-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.coords-input { width: 100%; box-sizing: border-box; min-height: 102px; resize: vertical; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: var(--space-2); font-family: var(--font-mono); font-size: var(--fs-xs); background: var(--color-bg-raised); color: var(--color-text); margin-bottom: var(--space-2); }
.row { display: flex; gap: var(--space-2); }
.primary, .secondary { padding: var(--space-2) var(--space-3); border-radius: var(--radius-sm); cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-raised); color: var(--color-text); font-size: var(--fs-xs); font-family: var(--font-sans); }
.primary { border-color: var(--color-accent); background: var(--color-accent); color: var(--color-accent-contrast); }
.primary:hover { background: var(--color-accent-hover); }
.secondary:hover { background: var(--color-bg-subtle); }
.error { margin: var(--space-2) 0 0; color: var(--color-error-text); font-size: var(--fs-sm); font-family: var(--font-sans); }
.success { margin: var(--space-2) 0 0; color: var(--color-success-text); font-size: var(--fs-sm); font-family: var(--font-sans); }
.preview-list { margin: var(--space-2) 0 0; padding-left: var(--space-3); font-size: var(--fs-xs); color: var(--color-text-muted); font-family: var(--font-mono); }
.preview-list li.bad { color: var(--color-error-text); }
.batch-preview { overflow-wrap: anywhere; margin-bottom: var(--space-2); }
.cap { margin: var(--space-2) 2px 0; font-size: var(--fs-xs); color: var(--color-text-muted); }
.feature-count-chip { display: inline-block; padding: var(--space-1) var(--space-2); border-radius: var(--radius-sm); background: var(--color-bg-subtle); border: 1px solid var(--color-border); font-size: var(--fs-xs); color: var(--color-text-muted); }

button:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }

button:disabled,
input:disabled,
select:disabled,
textarea:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
